import React, { JSX, useContext, useMemo } from "react";
import { cloneDeep, Dictionary, groupBy, orderBy } from "lodash";
import { ColumnConfig, SortDirections, TextAlignment, printNumberAsString } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { getNetworkTooltip } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/utility";
import { MedicaidNetwork } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidNetwork";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";
import ContractEntityProfile from "@app/projects/servicing-provider/provider-affiliations/ContractEntityProfile";
import { getClaimsVolumeDefinitions, getEfficiencyAndEffectivenessDefinitions } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { NPF } from "@app/projects/servicing-provider/types";
import { FlyoutLink } from "@app/shared/Flyouts";
import { BenchmarkingReportModal } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/BenchmarkingReportModal";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard/BenchmarkingContext";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingLineOfBusinessEnum";
import { BenchmarkingNetwork } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingNetwork";
import BenchmarkingFiltersOverview from "@app/overviews/network-benchmarking-dashboard/dashboard-details/BenchmarkingFiltersOverview";
import BenchmarkingProviderFlyout from "@app/overviews/network-benchmarking-dashboard/dashboard-details/BenchmarkingProviderFlyout";
import { useBenchmarkingDashboardDetails } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/hooks/useBenchmarkingDashboardDetails";
import { BenchmarkingDashboardDetails } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingDashboardDetails";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingDashboardDetailsTypeEnum";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonFiltersPayload";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";

interface BenchmarkingDashboardDetailsViewProps {
  filtersPayload: NetworkComparisonFiltersPayload;
  onBackToFullViewClick: () => void;
  totalCount: number;
}

interface BenchmarkingDashboardDetailsViewWaffleToppings {
  commercialClaimsLabel: string;
  effectivenessRank: string;
  efficiencyRank: string;
  entityType?: string;
  key: string;
  medicaidClaimsLabel: string;
  medicareClaimsLabel: string;
  networkCount: string;
  networkList: JSX.Element;
  primaryContractEntity?: JSX.Element;
  primaryInstitutionalAffiliation?: string;
  providerName: JSX.Element;
  specialtyGroupName: string;
  totalClaimsLabel: string;
}

const DashboardDetailsView = (props: BenchmarkingDashboardDetailsViewProps): JSX.Element => {
  const benchmarkingContext = useContext(BenchmarkingContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);

  const filtersPayload = useMemo(
    () => benchmarkingContext.getFormattedFiltersPayload(cloneDeep(props.filtersPayload)),
    [props.filtersPayload, benchmarkingContext.getFormattedFiltersPayload]
  );

  const { dashboardDetailsData, isLoading: loading } = useBenchmarkingDashboardDetails(benchmarkingFiltersContext.getService(), filtersPayload);

  const columnConfig: ColumnConfig = {
    commercialClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Commercial Claims Volume",
      noSort: props.totalCount > 500,
      testId: "commercial-claims-volume",
      tooltip: getClaimsVolumeDefinitions(),
    },
    effectivenessRank: {
      alignment: TextAlignment.Left,
      displayText: "Effectiveness",
      noSort: props.totalCount > 500,
      testId: "effectiveness",
      tooltip: getEfficiencyAndEffectivenessDefinitions(NPF.EffectivenessRank),
    },
    efficiencyRank: {
      alignment: TextAlignment.Left,
      displayText: "Efficiency",
      noSort: props.totalCount > 500,
      testId: "efficiency",
      tooltip: getEfficiencyAndEffectivenessDefinitions(NPF.EfficiencyRank),
    },
    ...(benchmarkingFiltersContext.isAffiliationState() && {
      entityType: {
        alignment: TextAlignment.Left,
        displayText: "Entity Type",
        testId: "entity-type",
      },
    }),
    medicaidClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Medicaid Claims Volume",
      noSort: props.totalCount > 500,
      testId: "medicaid-claims-volume",
      tooltip: getClaimsVolumeDefinitions(),
    },
    medicareClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Medicare Claims Volume",
      noSort: props.totalCount > 500,
      testId: "medicare-claims-volume",
      tooltip: getClaimsVolumeDefinitions(),
    },
    networkCount: {
      alignment: TextAlignment.Left,
      displayText: "Network Count",
      noSort: props.totalCount > 500,
      testId: "network-count",
      tooltip: "Count of selected networks a provider is contracted with",
    },
    networkList: {
      alignment: TextAlignment.Left,
      displayText: "Network List",
      noSort: true,
      testId: "network-list",
      tooltip: "List of all selected networks a provider is contracted with",
    },
    ...(benchmarkingFiltersContext.isAffiliationState() && {
      primaryContractEntity: {
        alignment: TextAlignment.Left,
        displayText: "Primary Contract Entity",
        testId: "primary-contract-entity",
      },
    }),
    ...(benchmarkingFiltersContext.isAffiliationState() && {
      primaryInstitutionalAffiliation: {
        alignment: TextAlignment.Left,
        displayText: "Primary Institutional Affiliation",
        testId: "primary-institutional-affiliation",
      },
    }),
    providerName: {
      alignment: TextAlignment.Left,
      displayText: "Name",
      noSort: props.totalCount > 500,
      testId: "name",
    },
    specialtyGroupName: {
      alignment: TextAlignment.Left,
      displayText: "Specialty",
      hidden: props.filtersPayload.specialtyGroupIds.length > 1,
      noSort: props.totalCount > 500,
      testId: "specialty-group-name",
    },
    totalClaimsLabel: {
      alignment: TextAlignment.Left,
      displayText: "Total Claims Volume",
      noSort: props.totalCount > 500,
      testId: "total-claims-volume",
      tooltip: getClaimsVolumeDefinitions(),
    },
  };

  const customSort = (
    records: BenchmarkingDashboardDetailsViewWaffleToppings[],
    column: keyof BenchmarkingDashboardDetailsViewWaffleToppings,
    sortDirection: SortDirections
  ): BenchmarkingDashboardDetailsViewWaffleToppings[] => {
    let sortedData: BenchmarkingDashboardDetailsViewWaffleToppings[] = [];

    const getDashboardDetail = (npi: string): BenchmarkingDashboardDetails => {
      return (dashboardDetailsData ?? []).find(x => x.npi === npi);
    }

    switch (column) {
      case "providerName":
        sortedData = orderBy(records, (d) => getDashboardDetail(d.key)?.providerName, sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "efficiencyRank":
      case "effectivenessRank":
      case "networkCount":
        sortedData = orderBy(records, (d) => getDashboardDetail(d.key)?.[column], sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "commercialClaimsLabel":
      case "medicaidClaimsLabel":
      case "medicareClaimsLabel":
      case "specialtyGroupName":
      case "totalClaimsLabel":
        sortedData = orderBy(records, (d) => d[column], sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "entityType":
        sortedData = orderBy(records, (d) => getDashboardDetail(d.key)?.entityType, sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "primaryContractEntity":
        sortedData = orderBy(records, (d) => getDashboardDetail(d.key)?.professionalContractEntityName, sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "primaryInstitutionalAffiliation":
        sortedData = orderBy(records, (d) => getDashboardDetail(d.key)?.primaryInstitutionalAffiliation, sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      default:
        break;
    }

    return sortedData;
  }

  const formatNetworkList = (networkList: string[], isFlyout?: boolean): JSX.Element => {
    const marketValue = "Market Data";
    const isMarketDrillIn = props.filtersPayload?.drillinOption === BenchmarkingDashboardDetailsTypeEnum.MarketOutOfNetworkDrillin ||
      props.filtersPayload?.drillinOption === BenchmarkingDashboardDetailsTypeEnum.NetworkInMarketDrillin || networkList.includes(marketValue);

    if (networkList.length === 1 &&
      networkList[0] === marketValue &&
      isMarketDrillIn &&
      !isFlyout
    ) {
      return (
        <span>
          NPI only in market data
        </span>
      );
    }

    const networks: BenchmarkingNetworkType[] = [];

    for (const networkId of networkList) {
      if (networkId === marketValue && isMarketDrillIn) {
        const placeholderMarketValue: BenchmarkingNetwork = {
          cmsCountyCount: null,
          contractId: null,
          counties: [],
          countyCount: null,
          isMarket: true,
          networkCounties: [],
          opportunityCountyCount: null,
          parentOrganization: "Market Data - QES",
          plans: [],
          versionId: null,
        }

        networks.push(placeholderMarketValue as BenchmarkingNetworkType);
      } else {
        const network: BenchmarkingNetworkType = benchmarkingFiltersContext.selectedNetworks.find(x => x.contractId === networkId);

        if (!!network) {
          networks.push(network);
        }
      }
    }

    const groupedNetworks: Dictionary<BenchmarkingNetworkType[]> = groupBy(networks, "parentOrganization");

    const getNetworkDisplay = (n: BenchmarkingNetworkType) => {
      switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
        case BenchmarkingLineOfBusinessEnum.Marketplace:
          return n?.isMarket ? marketValue : (
            <TooltipWrapper
              text={getNetworkTooltip(n as QHPNetwork)}
            >
              {(n as QHPNetwork).networkName}
            </TooltipWrapper>
          );
        case BenchmarkingLineOfBusinessEnum.Medicare:
          return n?.contractId;
        case BenchmarkingLineOfBusinessEnum.Medicaid:
          return n?.isMarket ? marketValue : (n as MedicaidNetwork).program;
        default:
          return;
      }
    }

    return (
      <React.Fragment>
        {Object.entries(groupedNetworks).map(([key, value], parentOrganizationIndex) => (
          <div key={parentOrganizationIndex}>
            <div>
              {key}
            </div>
            {value.map((network, networkIndex) => (
              <div className="ms-3" key={networkIndex}>
                {getNetworkDisplay(network)}
              </div>
            ))}
          </div>
        ))}
      </React.Fragment>
    );
  }

  const dashboardDetailsViewToppings: BenchmarkingDashboardDetailsViewWaffleToppings[] = (dashboardDetailsData ?? []).map((summary, index) => {
    /* oxlint-disable sort-keys */
    return {
      key: summary.npi,
      providerName: (
        <React.Fragment>
          <div className="d-block">
            <FlyoutLink
              classNames="qes-button qes-button-secondary"
              testId={`benchmarking-dashboard--dashboard-details__flyout-link-${index}`}
              content={{
                body: (
                  <BenchmarkingProviderFlyout
                    dashboardDetails={summary}
                    formatNetworkList={formatNetworkList}
                  />
                ),
                title: "",
              }}
            >
              {!!summary.providerName ? summary.providerName : summary.npi}
            </FlyoutLink>
          </div>
          {!!summary.providerName && (
            <div className="d-block text-muted">
              {summary.npi}
            </div>
          )}
        </React.Fragment>
      ),
      specialtyGroupName: summary.specialtyGroupName,
      effectivenessRank: !!summary.effectivenessRank ? summary.effectivenessRank.toString() : "---",
      efficiencyRank: !!summary.efficiencyRank ? summary.efficiencyRank.toString() : "---",
      commercialClaimsLabel: !!summary.commercialClaimsLabel ? summary.commercialClaimsLabel : "---",
      medicaidClaimsLabel: !!summary.medicaidClaimsLabel ? summary.medicaidClaimsLabel : "---",
      medicareClaimsLabel: !!summary.medicareClaimsLabel ? summary.medicareClaimsLabel : "---",
      totalClaimsLabel: !!summary.totalClaimsLabel ? summary.totalClaimsLabel : "---",
      networkCount: printNumberAsString(summary.networkCount, "---"),
      networkList: formatNetworkList(summary.networkList),
      primaryContractEntity: !!summary.professionalContractEntityName ? (
        <FlyoutLink
          disabled={!benchmarkingContext.affiliationsVersion || benchmarkingContext.affiliationsVersionLoading}
          content={{
            body:
              <ContractEntityProfile
                contractEntityId={summary.professionalAffiliationId ?? 0}
                contractEntityName={summary.professionalContractEntityName}
                providerEntityType={""}
                versionId={benchmarkingContext.affiliationsVersion}
                showPerformanceOverview={true}
              />,
            title: "Contract Entity Profile",
          }
          }
          classNames="qes-button-secondary"
          testId={`benchmarking-dashboard--dashboard-details__contract-entity-flyout-link-${index}`}
        >
          {summary.professionalContractEntityName}
        </FlyoutLink>
      ) : (
        <span>---</span>
      ),
      entityType: !!summary.entityType ? summary.entityType : "---",
      primaryInstitutionalAffiliation: !!summary.primaryInstitutionalAffiliation ? summary.primaryInstitutionalAffiliation : "---",
    };
    /* oxlint-enable sort-keys */
  });

  return (
    <React.Fragment>
      <PageHeader disableHR={true} title="Dashboard Details">
        <PageHeaderRight>
          <QESButton
            className="ms-3"
            onClick={() => props.onBackToFullViewClick()}
            qesTestId="benchmarking-dashboard__dashboard-details--back-to-full-view"
            qesType="secondary"
          >
            Back to Dashboard
          </QESButton>

          <BenchmarkingReportModal />
        </PageHeaderRight>
      </PageHeader>
      <BenchmarkingFiltersOverview />
      {(!loading && props.totalCount > 0) && (
        <div className="row">
          <div className="col-12">
            <label className="copy-text-default mb-3">
              You are viewing {printNumberAsString(dashboardDetailsViewToppings.length, "---")} of {printNumberAsString(props.totalCount, "---")} results.
              {props.totalCount > 500 && " To see all results, create a report using the options on the right."}
            </label>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-12">
          <Waffles<BenchmarkingDashboardDetailsViewWaffleToppings>
            columns={columnConfig}
            customSort={customSort}
            data={dashboardDetailsViewToppings}
            fullWidth={true}
            loading={loading}
            testId="benchmarking-dashboard__dashboard-details--table"
          />
        </div>
      </div>
    </React.Fragment>
  )
}

export default DashboardDetailsView;
